import {
    ChangeEvent,
    FormEvent,
    FunctionComponent,
    ReactNode,
    useCallback,
    useId,
    useMemo,
    useState,
} from 'react';
import styles from './styles.module.scss';
import { CademyError } from '@shared/domain-shared';
import Card from '../Card';
import Alert from '../Alert';
import Button from '../Button';
import { LinkButton } from '../LinkButton';
import { GiftCardLightIcon } from '../Icons';
import { OriginString } from '@shared/types';

export type VoucherInputProps = {
    courseId: string;
    educatorId: string;
    onVoucherSelected: (voucherId: string, voucherCode: string) => unknown;
    origin: OriginString;
};

export const VoucherInput: FunctionComponent<VoucherInputProps> = ({
    courseId,
    educatorId,
    onVoucherSelected,
    origin,
}) => {
    const id = useId();
    const [voucherCode, setVoucherCode] = useState('');

    const [isExpanded, setIsExpanded] = useState(false);
    const [isValidatingVoucher, setIsValidatingVoucher] = useState(false);
    const [voucherApplied, setVoucherApplied] = useState(false);
    const [validationError, setValidationError] = useState<CademyError | null>(null);

    const onSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            setIsValidatingVoucher(true);
            try {
                setValidationError(null);
                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_CADEMY_API_URL}/vouchers/validate`,
                    {
                        method: 'POST',
                        body: JSON.stringify({
                            code: voucherCode,
                            course_ref: courseId,
                            educator_ref: educatorId,
                            origin: origin,
                        }),
                    }
                );
                if (response.ok === false) {
                    if (response.headers.get('content-type') === 'application/problem+json') {
                        const problemDetails = await response.json();
                        const error = CademyError.fromObject(problemDetails);
                        throw error;
                    }
                    if (response.headers.get('content-type') === 'application/json') {
                        throw await response.json();
                    }
                    const error = await response.text();
                    throw new Error(error);
                }
                const { voucher_id } = await response.json();
                onVoucherSelected(voucher_id, voucherCode);
                setVoucherApplied(true);
            } catch (error) {
                setValidationError(CademyError.fromUnknown(error));
                setVoucherApplied(false);
            } finally {
                setIsValidatingVoucher(false);
            }
        },
        [voucherCode, onVoucherSelected, courseId, educatorId, origin]
    );

    const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setVoucherCode(event.target.value);
    }, []);

    const VoucherForm = useMemo((): ReactNode => {
        return (
            <form onSubmit={onSubmit} id={id}>
                <div className={styles.inputWithButton}>
                    <input
                        onChange={onInputChange}
                        type="text"
                        placeholder="XXXX XXXX XXXX XXXX"
                        aria-label="Promo code or gift card"
                    />
                    <Button preset="primary" type="submit" disabled={isValidatingVoucher}>
                        {isValidatingVoucher ? 'Applying...' : 'Apply'}
                    </Button>
                </div>
                {validationError ? (
                    <Alert preset="danger" className={styles.alert}>
                        {validationError.title} {validationError.detail || ''}
                    </Alert>
                ) : null}
                {voucherApplied ? (
                    <Alert preset="success" className={styles.alert}>
                        Voucher applied successfully!
                    </Alert>
                ) : null}
            </form>
        );
    }, [id, isValidatingVoucher, onInputChange, onSubmit, validationError, voucherApplied]);

    return (
        <Card className={styles.container}>
            <LinkButton
                onClick={() => setIsExpanded(!isExpanded)}
                className={styles.expandButton}
                aria-expanded={isExpanded}
                aria-controls={id}
            >
                <GiftCardLightIcon />
                <span>Apply promo code or gift card</span>
            </LinkButton>
            {isExpanded ? VoucherForm : null}
        </Card>
    );
};
